import { useState, useCallback, useMemo, useRef, memo } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  GridOn as GridOnIcon,
  ImageNotSupported as ImageNotSupportedIcon
} from '@mui/icons-material';
import { usePricingService } from '../hooks/usePricingService';
import { usePedidoStore } from '../stores/pedido.store';
import { useAuth } from '@/hooks/useAuth';
import { GradeProduto, type GradeVariante } from './GradeProduto';
import type { ProdutoPricing } from '../services/pricing.service';
import { agruparFaixasPreco, varianteToPricing } from '../services/grade-pricing';

// AIDEV-NOTE: Tipo completo de produto retornado pela API /api/produtos
export interface ProdutoCatalogo extends ProdutoPricing {
  REFERENCIA: string;
  DESCRICAO: string;
  SECAO?: string | null;
  GRUPO?: string | null;
  SUBGRUPO?: string | null;
  COR?: string | null;
  TAMANHO?: string | null;
  CODCOR?: number | null;
  CODTAMANHO?: number | null;
  SALDO_ESTOQUE: number;
  preco_custo?: number | null;
  codbarras?: string | null;
  // Variantes de grade (se o produto tem cores/tamanhos)
  variantes?: GradeVariante[];
}

interface CatalogoProdutoProps {
  produto: ProdutoCatalogo;
  /** URL da primeira foto do produto (thumbnail). Opcional, nao bloqueia rendering. */
  thumbnailUrl?: string;
}

// AIDEV-PERF: Instancia unica de formatador para evitar recriar em cada render
const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const formatCurrency = (value: number) => brlFormatter.format(value);

// AIDEV-NOTE: Card MUI que exibe um produto no catalogo.
// Mostra preco calculado, badge PROMO, saldo estoque, e permite adicionar ao pedido.
// AIDEV-PERF: React.memo previne re-renders desnecessarios ao rolar a lista virtualizada.
// Se o produto tem variantes (grade cor x tamanho), abre GradeProduto em Dialog.

export const CatalogoProduto = memo(function CatalogoProduto({
  produto,
  thumbnailUrl
}: CatalogoProdutoProps) {
  const { calcularPreco, isPromocaoAtiva } = usePricingService();
  const { user } = useAuth();
  const addItem = usePedidoStore((s) => s.addItem);
  const updateItem = usePedidoStore((s) => s.updateItem);
  const theme = useTheme();
  // AIDEV-NOTE: xs = mobile. Usado para (a) Dialog da grade em fullscreen e (b) mover o campo de
  // preco editavel para a linha de acoes — na coluna da direita ele esmagava a descricao.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [gradeOpen, setGradeOpen] = useState(false);
  const [qtdSimples, setQtdSimples] = useState(1);

  const temGrade = produto.variantes && produto.variantes.length > 0;

  // AIDEV-NOTE: Seletores primitivos (indice e quantidade no carrinho) para este produto.
  // Retornam numero -> com React.memo so re-renderiza este card quando a qtd dele muda,
  // sem re-renderizar toda a lista virtualizada a cada alteracao do carrinho.
  const carrinhoIndex = usePedidoStore(
    useCallback(
      (s) =>
        s.itens.findIndex(
          (i) =>
            i.produto_codigo === produto.CODIGO &&
            (i.codcor ?? 0) === (produto.CODCOR ?? 0) &&
            (i.codtamanho ?? 0) === (produto.CODTAMANHO ?? 0)
        ),
      [produto.CODIGO, produto.CODCOR, produto.CODTAMANHO]
    )
  );
  const qtdNoCarrinho = usePedidoStore(
    useCallback(
      (s) => {
        const it = s.itens.find(
          (i) =>
            i.produto_codigo === produto.CODIGO &&
            (i.codcor ?? 0) === (produto.CODCOR ?? 0) &&
            (i.codtamanho ?? 0) === (produto.CODTAMANHO ?? 0)
        );
        return it ? it.quantidade : 0;
      },
      [produto.CODIGO, produto.CODCOR, produto.CODTAMANHO]
    )
  );

  // AIDEV-NOTE: Preco unitario da linha no carrinho (null se nao esta) — reflete alteracao
  // manual feita aqui ou no carrinho, mantendo card e carrinho sincronizados.
  const valorNoCarrinho = usePedidoStore(
    useCallback(
      (s) => {
        const it = s.itens.find(
          (i) =>
            i.produto_codigo === produto.CODIGO &&
            (i.codcor ?? 0) === (produto.CODCOR ?? 0) &&
            (i.codtamanho ?? 0) === (produto.CODTAMANHO ?? 0)
        );
        return it ? it.valor_unitario : null;
      },
      [produto.CODIGO, produto.CODCOR, produto.CODTAMANHO]
    )
  );

  const estaNoCarrinho = carrinhoIndex >= 0;
  // AIDEV-NOTE: Quando o produto ja esta no carrinho, o campo reflete e edita a linha
  // existente (set, permite aumentar/diminuir). Caso contrario, usa estado local qtdSimples.
  const displayQtd = estaNoCarrinho ? qtdNoCarrinho : qtdSimples;

  // AIDEV-PERF: Memoizado para evitar recalculo em cada re-render do virtualizer
  const promocaoAtiva = useMemo(() => isPromocaoAtiva(produto), [isPromocaoAtiva, produto]);
  const preco = useMemo(
    () => calcularPreco(produto, displayQtd),
    [calcularPreco, produto, displayQtd]
  );

  // AIDEV-NOTE: Politica de alteracao de preco unitario (mesma do carrinho) — permite editar
  // o preco direto no card para produtos SEM grade, evitando ida ao carrinho.
  const podeAlterarPreco = !temGrade && (user?.pode_alterar_preco_unitario ?? false);
  const limiteReducaoPreco = user?.limite_reducao_preco_pct ?? null;
  const limiteAumentoPreco = user?.limite_aumento_preco_pct ?? null;

  // Preco customizado antes de adicionar ao carrinho (null = usa o calculado)
  const [precoCustom, setPrecoCustom] = useState<number | null>(null);
  // Preco efetivo: linha do carrinho > override local > preco calculado
  const precoEfetivo = valorNoCarrinho ?? precoCustom ?? preco.preco_unitario;

  // AIDEV-NOTE: produto sem preco nao pode ser adicionado — addItemSchema exige valor_unitario
  // .positive(), e um item zerado derruba o finalizar E o auto-save do rascunho. Usa o mesmo
  // valor que vai no item (preco editado no card tem precedencia), nao o PRECO_VENDA cru.
  const precoParaItem = precoCustom ?? preco.preco_unitario;
  const semPreco = !temGrade && !(precoParaItem > 0);

  const [localPreco, setLocalPreco] = useState(precoEfetivo.toFixed(2));
  const [precoError, setPrecoError] = useState(false);

  // Sincroniza estado local quando o preco efetivo muda externamente (carrinho, qtd, tabela)
  const precoEfetivoRef = useRef(precoEfetivo);
  if (precoEfetivo !== precoEfetivoRef.current) {
    precoEfetivoRef.current = precoEfetivo;
    setLocalPreco(precoEfetivo.toFixed(2));
    setPrecoError(false);
  }

  // Limites relativos ao preco calculado pela tabela (mesma semantica do CarrinhoItem)
  const precoMin =
    limiteReducaoPreco !== null ? preco.preco_unitario * (1 - limiteReducaoPreco / 100) : 0.01;
  const precoMax =
    limiteAumentoPreco !== null ? preco.preco_unitario * (1 + limiteAumentoPreco / 100) : undefined;

  const precoFoiAlterado =
    podeAlterarPreco && Math.abs(precoEfetivo - preco.preco_unitario) > 0.001;

  const handlePrecoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalPreco(e.target.value);
    setPrecoError(false);
  }, []);

  const handlePrecoBlur = useCallback(() => {
    // AIDEV-NOTE: Arredonda para 2 casas antes de validar/salvar — preco de venda usa 2 decimais
    const v = Math.round(parseFloat(localPreco) * 100) / 100;
    if (
      isNaN(v) ||
      v <= 0 ||
      v < precoMin - 0.001 ||
      (precoMax !== undefined && v > precoMax + 0.001)
    ) {
      setLocalPreco(precoEfetivo.toFixed(2));
      setPrecoError(true);
      return;
    }
    setPrecoError(false);
    setLocalPreco(v.toFixed(2));
    if (estaNoCarrinho) {
      updateItem(carrinhoIndex, { valor_unitario: v });
    } else {
      setPrecoCustom(v);
    }
  }, [localPreco, precoMin, precoMax, precoEfetivo, estaNoCarrinho, carrinhoIndex, updateItem]);

  const tooltipPreco = [
    `Preço tabela: ${formatCurrency(preco.preco_unitario)}`,
    limiteReducaoPreco !== null
      ? `Redução máxima: ${limiteReducaoPreco}%`
      : 'Sem limite de redução',
    limiteAumentoPreco !== null ? `Aumento máximo: ${limiteAumentoPreco}%` : 'Sem limite de aumento'
  ].join(' · ');

  // AIDEV-NOTE: faixas de preco por tamanho (grade) — null quando preco unico (exibicao atual)
  const faixasLabel = useMemo(() => {
    if (!temGrade) return null;
    return agruparFaixasPreco(
      produto.variantes!,
      (v) => calcularPreco(varianteToPricing(produto, v), 1).preco_unitario,
      formatCurrency
    );
  }, [temGrade, produto, calcularPreco]);

  // AIDEV-NOTE: Preco original para exibir riscado quando em promocao
  const precoOriginal = promocaoAtiva ? produto.PRECO_VENDA : null;

  const handleAdicionarSimples = useCallback(() => {
    if (qtdSimples <= 0) return;
    // AIDEV-NOTE: guarda redundante ao disabled do botao — protege chamada programatica
    if (!(precoParaItem > 0)) return;

    addItem({
      produto_codigo: produto.CODIGO,
      referencia: produto.REFERENCIA,
      descricao: produto.DESCRICAO,
      cor: produto.COR ?? null,
      tamanho: produto.TAMANHO ?? null,
      quantidade: qtdSimples,
      // AIDEV-NOTE: usa o preco editado no card (se houver) — senao o calculado pela tabela
      valor_unitario: precoCustom ?? preco.preco_unitario,
      desconto_percentual: 0,
      custo: produto.preco_custo ?? null,
      codbarras: produto.codbarras ?? null,
      codcor: produto.CODCOR ?? 0,
      codtamanho: produto.CODTAMANHO ?? 0
    });

    setQtdSimples(1);
  }, [addItem, produto, preco, qtdSimples, precoCustom, precoParaItem]);

  // AIDEV-NOTE: Se o item ja esta no carrinho, edita direto a linha (set qtd, permite diminuir).
  // Senao, ajusta apenas o estado local que sera usado ao clicar em "+".
  const handleQtdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseInt(e.target.value, 10);
      if (isNaN(v) || v < 1) return;
      if (estaNoCarrinho) {
        updateItem(carrinhoIndex, { quantidade: v });
      } else {
        setQtdSimples(v);
      }
    },
    [estaNoCarrinho, carrinhoIndex, updateItem]
  );

  // AIDEV-NOTE: Campo de preco editavel (politica pode_alterar_preco_unitario) — laranja quando
  // alterado, mesmo padrao visual do CarrinhoItem. Uma unica instancia: fica na coluna da direita
  // no desktop e na linha de acoes no mobile, nunca nas duas (duplicar quebraria foco/estado).
  const precoEditavelField = (
    <Tooltip title={tooltipPreco} placement="top">
      <TextField
        type="number"
        size="small"
        label="Preço"
        value={localPreco}
        onChange={handlePrecoChange}
        onBlur={handlePrecoBlur}
        onFocus={(e) => e.target.select()}
        error={precoError}
        inputProps={{
          min: precoMin,
          max: precoMax,
          step: 0.01,
          inputMode: 'decimal',
          style: { textAlign: 'center' }
        }}
        sx={{
          width: { xs: 112, sm: 124 },
          flexShrink: 0,
          '& .MuiOutlinedInput-root':
            precoFoiAlterado && !precoError
              ? {
                  '& fieldset': { borderColor: 'warning.main' },
                  '&:hover fieldset': { borderColor: 'warning.dark' }
                }
              : {}
        }}
        InputProps={{
          sx:
            precoFoiAlterado && !precoError
              ? { color: 'warning.main', fontWeight: 600 }
              : { color: 'primary.main', fontWeight: 600 }
        }}
      />
    </Tooltip>
  );

  const handleAbrirGrade = useCallback(() => setGradeOpen(true), []);
  const handleFecharGrade = useCallback(() => setGradeOpen(false), []);

  return (
    <>
      <Card variant="outlined" sx={{ mb: 1 }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {/* AIDEV-NOTE: Thumbnail da primeira foto do produto — opcional, nao bloqueia */}
            <Box
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                mr: 1.5,
                borderRadius: 1,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.50'
              }}
            >
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt=""
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                <ImageNotSupportedIcon sx={{ color: 'grey.400', fontSize: 24 }} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* AIDEV-NOTE: Fonte menor no mobile (xs) para descricoes longas ocuparem menos linhas;
                  mantem o body1 padrao no desktop (sm+). */}
              <Typography
                variant="body1"
                fontWeight={500}
                sx={{
                  fontSize: { xs: '0.8125rem', sm: '1rem' },
                  lineHeight: { xs: 1.3, sm: 1.5 }
                }}
              >
                {produto.DESCRICAO}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Ref: {produto.REFERENCIA}
                </Typography>
                {produto.COR && (
                  <Typography variant="caption" color="text.secondary">
                    Cor: {produto.COR}
                  </Typography>
                )}
                {produto.TAMANHO && (
                  <Typography variant="caption" color="text.secondary">
                    Tam: {produto.TAMANHO}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* AIDEV-NOTE: display flex/column obrigatorio — o TextField do MUI e inline-flex e,
                em bloco, dividia a linha com o caption de estoque, inflando a largura da coluna
                para ~196px e sobrando ~60px para a descricao no mobile. */}
            <Box
              sx={{
                textAlign: 'right',
                ml: { xs: 1, sm: 2 },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}
              >
                {promocaoAtiva && (
                  <Chip label="PROMO" color="error" size="small" sx={{ fontWeight: 600 }} />
                )}
              </Box>
              {precoOriginal != null && (
                <Typography
                  variant="body2"
                  sx={{ textDecoration: 'line-through', color: 'text.disabled' }}
                >
                  {formatCurrency(precoOriginal)}
                </Typography>
              )}
              {faixasLabel ? (
                <Typography
                  variant="subtitle2"
                  color="primary.main"
                  fontWeight={600}
                  sx={{ maxWidth: 180, lineHeight: 1.3 }}
                >
                  {faixasLabel}
                </Typography>
              ) : podeAlterarPreco ? (
                // No mobile o campo desce para a linha de acoes (abaixo)
                isMobile ? null : (
                  <Box sx={{ mt: 0.5 }}>{precoEditavelField}</Box>
                )
              ) : (
                <Typography variant="h6" color="primary.main" fontWeight={600}>
                  {formatCurrency(preco.preco_unitario)}
                </Typography>
              )}
              {user?.exibir_estoque_vendedor !== false && (
                <Typography
                  variant="caption"
                  sx={{
                    color: produto.SALDO_ESTOQUE === 0 ? 'error.main' : 'text.secondary',
                    fontWeight: produto.SALDO_ESTOQUE === 0 ? 600 : 400
                  }}
                >
                  Estoque: {produto.SALDO_ESTOQUE}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Acoes: adicionar simples ou abrir grade */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 1,
              flexWrap: { xs: 'wrap', sm: 'nowrap' }
            }}
          >
            {temGrade ? (
              <Button
                size="small"
                variant="outlined"
                onClick={handleAbrirGrade}
                startIcon={<GridOnIcon />}
              >
                Selecionar grade
              </Button>
            ) : (
              <>
                {podeAlterarPreco && isMobile && precoEditavelField}
                <TextField
                  type="number"
                  size="small"
                  value={displayQtd}
                  onChange={handleQtdChange}
                  // AIDEV-NOTE: select() ao focar -> digitar substitui o valor sem precisar apagar
                  onFocus={(e) => e.target.select()}
                  inputProps={{
                    min: 1,
                    inputMode: 'numeric',
                    style: { textAlign: 'center', width: 50 }
                  }}
                  sx={{ flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                />
                {estaNoCarrinho ? (
                  // AIDEV-NOTE: Item ja no carrinho — indicador (nao soma mais); ajuste e feito no campo acima
                  <IconButton
                    size="small"
                    disabled
                    aria-label="No carrinho"
                    sx={{
                      bgcolor: 'success.main',
                      color: 'white',
                      '&.Mui-disabled': { bgcolor: 'success.main', color: 'white', opacity: 0.85 }
                    }}
                  >
                    <CheckIcon />
                  </IconButton>
                ) : (
                  <Tooltip title={semPreco ? 'Produto sem preço cadastrado' : ''} describeChild>
                    {/* AIDEV-NOTE: span necessario — Tooltip do MUI nao dispara em elemento disabled */}
                    <span>
                      <IconButton
                        color="primary"
                        aria-label="Adicionar ao pedido"
                        disabled={semPreco}
                        onClick={handleAdicionarSimples}
                        size="small"
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Typography variant="caption" color="text.secondary">
                  {/* AIDEV-NOTE: Subtotal usa o preco efetivo (inclui alteracao manual no card) */}
                  Subtotal: {formatCurrency(precoEfetivo * displayQtd)}
                </Typography>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* AIDEV-NOTE: Grade em Dialog para evitar reflow na lista virtualizada e suportar mobile.
          fullScreen em telas pequenas garante espaco suficiente para preencher a tabela. */}
      {temGrade && (
        <Dialog
          open={gradeOpen}
          onClose={handleFecharGrade}
          fullScreen={isMobile}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              m: { xs: 1, sm: 2 },
              maxHeight: { xs: '95dvh', sm: '80vh' }
            }
          }}
        >
          <DialogTitle sx={{ pb: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {produto.DESCRICAO}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ref: {produto.REFERENCIA} — {formatCurrency(preco.preco_unitario)} / un.
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <GradeProduto
              produto={produto}
              variantes={produto.variantes!}
              onClose={handleFecharGrade}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
});
